import React, { useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import withInspector from 'fulogics-ui-web/dist/layout/inspector/hoc/withInspector';
import classNames from 'classnames';
import { IdType } from 'fulogics-ui-core/dist/api/models/IdType';
import { useId } from 'react-id-generator';
import { Popover } from 'devextreme-react';
import Button from 'fulogics-ui-web/dist/layout/dx/Button/Button';
import { connect } from 'react-redux';
import { iRootDispatch, iRootState } from '../../../store/store';
import ErrorIcon from '../../assets/svg/ErrorIcon';
import enOrderState from '../../../api/enums/enOrderState';
import enOrderError from '../../../api/enums/enOrderError';
import style from './OrderErrorInfo.module.scss';
import * as ids from './inspectorOptions';

export const getUniqueRules = (validationResult?: OrderValidationResult) => {
    if (!validationResult?.brokenRules?.length) {
        return [];
    }

    return Array.from(new Set(validationResult?.brokenRules.map(({ type }) => type)));
};

interface OrdersScreenDispatchProps {}

const mapStateToProps = (
    state: iRootState,
    ownProps: OrderErrorInfoProps
): OrderErrorInfoProps => ({
    currentUserId: state?.Identity?.userProfile?.id,
});

const mapDispatch = (_dispatch: iRootDispatch, _ownDispatch: OrdersScreenDispatchProps) => ({});

const OrderErrorInfo = connect(
    mapStateToProps,
    mapDispatch
)(
    memo(
        ({
            data,
            className,
            popoverContainerClassName,
            currentUserId,
            hideOnHoverEnd,
            hideEditButton,
            hideTitle,
            onEditClick,
        }: OrderErrorInfoProps) => {
            const { t } = useTranslation(['OrderErrorInfo', 'ErrorType']);
            const [targetId] = useId();

            const showIcon = !data?.validationResult?.isValid;

            const handleEditClick = useCallback(() => {
                onEditClick?.(data?.id);
            }, [data?.id, , onEditClick]);

            const canEdit =
                data?.state === enOrderState.Draft &&
                (!data?.currentEditing?.editingBy?.id ||
                    data?.currentEditing?.editingBy?.id === currentUserId);

            const hideEvent = hideOnHoverEnd ? 'dxhoverend' : 'dxclick';

            return (
                <>
                    {showIcon && (
                        <ErrorIcon id={targetId} className={classNames(style.icon, className)} />
                    )}
                    <Popover
                        target={`#${targetId}`}
                        showEvent="dxhoverstart"
                        hideEvent={hideEvent}
                        width="500px"
                        height="auto"
                    >
                        <div
                            className={classNames(style.popupContainer, popoverContainerClassName)}
                        >
                            {!hideTitle && (
                                <label className={classNames(style.title)}>{t('title')}</label>
                            )}
                            <div className={classNames(style.errorInfoWrapper)}>
                                {getUniqueRules(data?.validationResult)?.map((type, index) => (
                                    <label
                                        className={classNames(style.errorInfo)}
                                        key={`${targetId}-${type}-${index}`}
                                    >
                                        - {t(`ErrorType:${type}`)}
                                    </label>
                                ))}
                            </div>
                            {!hideEditButton && canEdit && (
                                <Button
                                    className={classNames(style.editButton)}
                                    inspectorId={ids.orderErrorInfoEditButtonId}
                                    onClick={handleEditClick}
                                />
                            )}
                        </div>
                    </Popover>
                </>
            );
        }
    )
);
export type OrderErrorInfoProps = {
    data?: OrderErrorInfoData;
} & OrderErrorInfoDisplayOptions;

export interface OrderErrorInfoDisplayOptions {
    className?: string;
    popoverContainerClassName?: string;
    //
    currentUserId?: IdType;
    hideEditButton?: boolean;
    hideOnHoverEnd?: boolean;
    hideTitle?: boolean;
    //
    onEditClick?: (orderId?: IdType) => void;
}

export type OrderValidationResult = {
    isValid?: boolean;
    brokenRules?: {
        message?: string;
        relatedEntityId?: IdType;
        type?: enOrderError;
    }[];
};

export type OrderErrorInfoData = {
    id?: IdType;
    state?: enOrderState;
    currentEditing?: {
        editingBy?: {
            id?: IdType;
        };
    };
    validationResult?: OrderValidationResult;
};

export const OrderErrorInfoDataKeys = [
    'id',
    'state',
    'validationResult.isValid',
    'validationResult.brokenRules.message',
    'validationResult.brokenRules.relatedEntityId',
    'validationResult.brokenRules.type',
    'currentEditing.editingBy.id',
];

export default withInspector<OrderErrorInfoProps>(OrderErrorInfo, {});
